<template>
  <v-row no-gutters class="fill-height pa-4 session-page">
    <!-- LEFT: Sessions -->
    <v-col cols="12" md="6" class="pr-md-3 mb-4 mb-md-0">
      <v-card class="panel" outlined>
        <v-card-title class="panel__title">
          <div>
            <div class="text-h6 font-weight-bold">User Sessions</div>
            <div class="caption grey--text text--darken-1">Tap a row to see the timeline</div>
          </div>
          <v-spacer />
          <v-chip small outlined class="font-weight-medium">
            {{ userSessionList.length }} Sessions
          </v-chip>
        </v-card-title>

        <v-divider />

        <v-data-table class="sessions-table" @click:row="getTimeline" fixed-header :items-per-page="-1"
          hide-default-footer item-key="sessionId" single-select sort-by="startTime" :sort-desc="true"
          :height="kioskAnalyticsDimension.userSessionheight - 65" :headers="userSessionHeader"
          :items="userSessionList">
          <template v-slot:item.count="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="26" class="mr-2 avatar-gradient">
                <v-icon small color="white">mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold text--primary">
                  {{ item.sessionId }}
                </div>
                <div class="caption grey--text">
                  {{ item.startTime | formatDate }} • {{ item.startTime | formatTime }}
                </div>
              </div>
            </div>
          </template>
          <template v-slot:item.userSessionStartTime="{ item }">
            <span class="mono">{{ item.startTime | formatTime }}</span>
          </template>

          <template v-slot:item.userSessionEndTime="{ item }">
            <span class="mono">{{ item.endTime | formatTime }}</span>
          </template>

          <template v-slot:item.userSessionDuration="{ item }">
            <v-chip x-small class="chip-duration" outlined>
              <v-icon x-small class="mr-1">mdi-timer-outline</v-icon>
              {{ getDuration(item.startTime, item.endTime) }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="pa-8 text-center grey--text">
              <v-icon large class="mb-2">mdi-database-off</v-icon>
              <div>No sessions found.</div>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- RIGHT: Timeline -->
    <v-col cols="12" md="6" class="pl-md-3">
      <v-card class="panel" outlined :height="kioskAnalyticsDimension.userSessionheight + 20">
        <v-card-title class="panel__title">
          <div>
            <div class="text-h6 font-weight-bold primary--text">Session Timeline</div>
            <div class="caption grey--text text--darken-1">
              <span v-if="selectedSessionId">Session: <span class="mono">{{ selectedSessionId }}</span></span>
              <span v-else>Select a session to view the timeline</span>
            </div>
          </div>
          <v-spacer />
          <v-chip v-if="timeline" small color="primary" outlined class="font-weight-medium">
            Active
          </v-chip>
        </v-card-title>

        <v-divider />

        <div class="timeline-wrap" :style="{ height: (kioskAnalyticsDimension.userSessionheight - 30) + 'px' }">
          <div v-if="timeline === null" class="empty-state">
            <v-icon large class="mb-3">mdi-hand-pointing-left</v-icon>
            <div class="text-subtitle-2">Select a session from the table</div>
            <div class="caption grey--text">Timeline will appear here</div>
          </div>

          <div v-else class="pa-4 overflow-y-auto fill-height">
            <div v-if="timeline.length === 0" class="empty-state">
              <v-icon large class="mb-3">mdi-account-alert</v-icon>
              <div class="text-subtitle-2">User detected</div>
              <div class="caption grey--text">No interaction took place.</div>
            </div>

            <v-timeline v-else dense clipped class="my-1">
              <div v-for="(item, i) in timeline" :key="i">
                <!-- Started -->
                <v-timeline-item v-if="item.recognitionType === 'Face' || item.recognitionType === 'Touch'"
                  :key="'start-' + i" color="success" icon="mdi-login-variant" class="mb-4" fill-dot>
                  <div class="d-flex align-center">
                    <div class="font-weight-bold">Session Started</div>
                    <v-spacer />
                    <v-chip x-small class="chip-soft" outlined>
                      <v-icon x-small class="mr-1">mdi-eye-outline</v-icon>
                      {{ item.recognitionType }}
                    </v-chip>
                  </div>
                  <v-divider class="mt-3"></v-divider>
                </v-timeline-item>

                <!-- Touch -->
                <v-timeline-item v-else-if="item.interactionType === 'Touch'" :key="'touch-' + i"
                  color="orange lighten-2" icon="mdi-gesture-tap" class="mb-4" fill-dot>
                  <div class="d-flex align-center mb-2">
                    <div class="font-weight-bold">Touch Interaction</div>
                    <v-spacer />
                    <v-chip x-small color="orange lighten-5" class="orange--text text--darken-3" outlined>
                      <v-icon x-small class="mr-1">mdi-view-dashboard-outline</v-icon>
                      {{ item.module || "Unknown Module" }}
                    </v-chip>
                  </div>

                  <v-card class="event-card" outlined>
                    <v-card-text class="py-3">
                      <div class="caption grey--text text--darken-1 mb-1">Content</div>
                      <div class="text-body-2">
                        {{ item.elementContent || "-" }}
                      </div>
                    </v-card-text>
                  </v-card>

                  <v-divider class="mt-3"></v-divider>
                </v-timeline-item>

                <!-- Speech -->
                <v-timeline-item v-else-if="item.interactionType === 'Speech'" :key="'speech-' + i"
                  color="indigo lighten-2" icon="mdi-microphone" class="mb-4" fill-dot>
                  <div class="d-flex align-center mb-2">
                    <div class="font-weight-bold">Speech Interaction</div>
                    <v-spacer />
                    <v-chip x-small color="indigo lighten-5" class="indigo--text text--darken-3" outlined>
                      <v-icon x-small class="mr-1">mdi-help-circle-outline</v-icon>
                      Q&A
                    </v-chip>
                  </div>

                  <v-card class="event-card" outlined>
                    <v-card-text class="py-3">
                      <div class="caption grey--text text--darken-1">Question</div>
                      <div class="text-body-2 font-weight-medium mb-2">
                        {{ (item.elementContent && item.elementContent.question) || "-" }}
                      </div>

                      <div class="caption grey--text text--darken-1">Answer</div>
                      <div class="text-body-2">
                        {{ (item.elementContent && item.elementContent.answer) || "-" }}
                      </div>
                    </v-card-text>
                  </v-card>

                  <v-divider class="mt-3"></v-divider>
                </v-timeline-item>
              </div>

              <!-- End marker (don’t use item === {}) -->
              <v-timeline-item v-if="selectedSessionId" color="grey" icon="mdi-logout" class="mb-1" fill-dot>
                <div class="font-weight-bold">Session Ended</div>
              </v-timeline-item>
            </v-timeline>
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "userSession",
  data() {
    return {
      timeline: null,
      selectedSessionId: null,
      userSessionHeader: [
        { text: "Session", value: "count", width: 260 },
        { text: "Start", value: "userSessionStartTime", width: 90 },
        { text: "End", value: "userSessionEndTime", width: 90 },
        { text: "Duration", value: "userSessionDuration", align: "end", width: 120 },
      ],
    };
  },
  computed: {
    ...mapGetters(["kioskAnalyticsDimension", "getSessionList"]),
    userSessionList() {
      return (this.getSessionList && this.getSessionList.data) ? this.getSessionList.data : [];
    },
  },
  methods: {
    getTimeline(item, row) {
      this.timeline = Array.isArray(item.payload) ? item.payload : [];
      this.selectedSessionId = item.sessionId;
      if (row && typeof row.select === "function") row.select(true);
    },
    getDuration(startTime, endTime) {
      if (!startTime || !endTime) return "-";

      const start = moment(startTime);
      const end = moment(endTime);

      if (!start.isValid() || !end.isValid()) return "-";

      const duration = moment.duration(end.diff(start));

      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      // HH:mm:ss if > 1 hour, else mm:ss
      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }

      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
  },
  filters: {
    formatStringTime(val) {
      if (!val) return "-";
      return moment(val).format("LTS");
    },
    formatTime(val) {
      if (!val) return "-";
      return moment(val).format("LT");
    },
    formatDate(val) {
      if (!val) return "-";
      return moment(val).format("L");
    },
    formatDuration(val) {
      if (!val) return "-";
      return moment(val).format("mm:ss");
    },
  },
};
</script>

<style scoped>
/* .session-page {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.06), rgba(0, 0, 0, 0.02));
} */

/* Cards */
.panel {
  border-radius: 14px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.panel__title {
  background: linear-gradient(90deg, rgba(25, 118, 210, 0.10), rgba(25, 118, 210, 0.02));
}

/* Table */
.sessions-table>>>.v-data-table__wrapper {
  border-radius: 0 0 14px 14px;
}

.sessions-table>>>tbody tr {
  transition: transform 0.08s ease, box-shadow 0.08s ease, background 0.08s ease;
}

.sessions-table>>>tbody tr:hover {
  cursor: pointer;
  background: rgba(25, 118, 210, 0.06) !important;
}

.sessions-table>>>tr.v-data-table__selected {
  background: rgba(25, 118, 210, 0.14) !important;
  color: #0d47a1 !important;
}

.sessions-table>>>tr.v-data-table__selected td {
  color: #0d47a1 !important;
  font-weight: 700;
}

/* Chips */
.chip-soft {
  background: rgba(25, 118, 210, 0.06);
}

.chip-duration {
  background: rgba(76, 175, 80, 0.06);
}

/* Avatar */
.avatar-gradient {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
}

/* Timeline */
.timeline-wrap {
  position: relative;
}

.event-card {
  border-radius: 12px;
  background: #fff;
}

.empty-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 24px;
  color: rgba(0, 0, 0, 0.6);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
